import React, { useState, useEffect } from 'react';
import { dailySpotAPI, dailyContractsAPI } from '@/services/commentary.service';

interface DailySpotFormProps {
  initialData?: any;
  onSave?: (spot: any, contracts: any | null) => void;
  onCancel?: () => void;
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SPOT_FIELDS: { key: string; label: string; unit: string }[] = [
  { key: 'spot_ethylene', label: 'Spot Ethylene', unit: '$USD' },
  { key: 'spot_rgp', label: 'Spot RGP', unit: '$USD' },
  { key: 'spot_pgp', label: 'Spot PGP', unit: '$USD' },
  { key: 'spot_benzene', label: 'Spot Benzene', unit: '$USD' },
  { key: 'spot_styrene', label: 'Spot Styrene', unit: '$USD' },
  { key: 'spot_butadiene', label: 'Spot Butadiene', unit: '$USD' },
  { key: 'vam', label: 'VAM', unit: '$USD' },
];

const CONTRACT_FIELDS: { key: string; label: string; unit: string }[] = [
  { key: 'ethylene_contract', label: 'Ethylene Contract', unit: '$USD' },
  { key: 'pgp_contract', label: 'PGP Contract', unit: '$USD' },
  { key: 'benzene_contract', label: 'Benzene Contract', unit: '$USD' },
  { key: 'styrene_contract', label: 'Styrene Contract', unit: '$USD' },
  { key: 'butadiene_contract', label: 'Butadiene Contract', unit: '$USD' },
];

const DailySpotForm: React.FC<DailySpotFormProps> = ({ initialData, onSave, onCancel }) => {
  const [date, setDate] = useState<string>(initialData?.date ?? today());
  const [spotValues, setSpotValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    SPOT_FIELDS.forEach(f => { init[f.key] = initialData?.[f.key] ?? ''; });
    return init;
  });
  const [contractValues, setContractValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    CONTRACT_FIELDS.forEach(f => { init[f.key] = initialData?.[f.key] ?? ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasContractValues = CONTRACT_FIELDS.some(f => contractValues[f.key]?.trim() !== '');

  const handleSpotChange = (key: string, value: string) => {
    setSpotValues(prev => ({ ...prev, [key]: value }));
  };

  const handleContractChange = (key: string, value: string) => {
    setContractValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!date) {
      setError('Date is required');
      return;
    }
    const missingSpot = SPOT_FIELDS.filter(f => !spotValues[f.key]?.trim());
    if (missingSpot.length > 0) {
      setError(`Required: ${missingSpot.map(f => f.label).join(', ')}`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const spotPayload: any = { date };
      SPOT_FIELDS.forEach(f => { spotPayload[f.key] = spotValues[f.key]?.trim() || null; });
      await dailySpotAPI.upsert(spotPayload);

      let contractPayload: any | null = null;
      if (hasContractValues) {
        const contractDate = date.substring(0, 7) + '-15';
        contractPayload = { date: contractDate };
        CONTRACT_FIELDS.forEach(f => { contractPayload[f.key] = contractValues[f.key]?.trim() || null; });
        await dailyContractsAPI.upsert(contractPayload);
      }

      onSave?.(spotPayload, contractPayload);
    } catch (err: any) {
      setError(err?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {error && (
        <div className="mb-4 px-4 py-2 bg-rs-red/20 border border-rs-red rounded text-rs-red text-sm">
          {error}
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-rs-off-white/70 mb-1">Date</label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-48 bg-rs-jet border border-rs-jet-light rounded px-3 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
        />
      </div>

      <div className="mb-6">
        <h3 className="text-rs-green font-semibold text-sm mb-3 uppercase tracking-wide">
          Spot Prices
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {SPOT_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-xs text-rs-off-white/60 mb-1">
                {field.label}
                <span className="ml-1 text-rs-off-white/40">{field.unit}</span>
                <span className="ml-1 text-rs-red">*</span>
              </label>
              <input
                type="number"
                step="0.0001"
                value={spotValues[field.key]}
                onChange={e => handleSpotChange(field.key, e.target.value)}
                placeholder="0.0000"
                className="w-full bg-rs-jet border border-rs-jet-light rounded px-3 py-2 text-rs-off-white focus:border-rs-green focus:outline-none text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-rs-off-white/60 font-semibold text-sm mb-1 uppercase tracking-wide">
          Contract Prices
          <span className="ml-2 text-rs-off-white/40 normal-case font-normal">(optional — leave blank if not releasing)</span>
        </h3>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {CONTRACT_FIELDS.map(field => (
            <div key={field.key}>
              <label className="block text-xs text-rs-off-white/60 mb-1">
                {field.label}
                <span className="ml-1 text-rs-off-white/40">{field.unit}</span>
              </label>
              <input
                type="number"
                step="0.0001"
                value={contractValues[field.key]}
                onChange={e => handleContractChange(field.key, e.target.value)}
                placeholder="—"
                className="w-full bg-rs-jet border border-rs-jet-light rounded px-3 py-2 text-rs-off-white focus:border-rs-green focus:outline-none text-sm"
              />
            </div>
          ))}
        </div>
        {hasContractValues && (
          <p className="mt-2 text-xs text-rs-off-white/50">
            Contract data will be staged for {date.substring(0, 7)}-15 when saved.
          </p>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-rs-green hover:bg-rs-light-green !text-rs-off-black font-semibold px-5 py-2 rounded transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Review & Publish'}
        </button>
        <button
          onClick={onCancel}
          className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-5 py-2 rounded transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DailySpotForm;
